import React from 'react'

import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import {
	determineLinkVariant,
	getLinkExceptions,
	renderContent,
} from '@/components/elementary/AppLink/utils'
import { buttonVariants } from '@/components/ui/button'
import { isAppLink, Link } from '@/lib/navigation'
import { cn } from '@/lib/styles'

const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
	(
		{
			href,
			label,
			className,
			children,
			iconRight,
			iconLeft,
			openExternalInNewTab = false,
			size = 'none',
			underline = false,
			...props
		},
		ref,
	) => {
		const variant = determineLinkVariant(href, label || children?.toString(), props.variant)
		const combinedClassName = cn(buttonVariants({ variant, size }), className)
		const linkClassName = cn(
			'group/app-link flex relative flex-row items-center gap-1 h-fit',
			underline && 'group-hover/image-link:border-b-brand-black hover:border-b-brand-black',
			variant !== 'google' && 'border-b border-transparent',
			combinedClassName,
		)

		const linkException = getLinkExceptions(
			{
				href,
				ref,
				linkClassName,
				props,
			},
			() => renderContent({ variant, iconLeft, iconRight, label, children }),
		)
		if (linkException) {
			return linkException
		}

		const isExternal = typeof href === 'string' && !isAppLink(href)
		if (isExternal) {
			return (
				<a
					href={`${new URL(href)}`}
					ref={ref}
					target={openExternalInNewTab ? '_blank' : ''}
					rel={openExternalInNewTab ? 'noopener noreferrer' : ''}
					className={linkClassName}
					{...props}
				>
					{renderContent({ variant, iconLeft, iconRight, label, children })}
				</a>
			)
		}

		return (
			<Link href={href} ref={ref} className={linkClassName} {...props}>
				{renderContent({ variant, iconLeft, iconRight, label, children })}
			</Link>
		)
	},
)

const AppLinkSSRFallback = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => (
	<AppLinkComponent {...props} ref={ref} />
))

export default AppLinkSSRFallback
